import { JSX, useContext, useEffect, useState } from "react";
import { ClaimsInsightsScoreSummaryModel } from "@app/projects/network-assessment/pci-dashboard/claims-insights";
import { PciDashboardFiltersContext, PciDashboardFiltersPayload, PciDashboardSummaryType } from "@app/projects/network-assessment/pci-dashboard/shared";
import { getRoundingDisplay } from "@app/projects/network-assessment/pci-dashboard/shared/utility";
import { DashboardTile, QESButton } from "@app/shared/qes-standards";
import { ColumnConfig, SortDirections, TextAlignment, Waffles } from "@app/shared/waffles";
import { EllipsisTooltip, LoadingSpinner, formatDateTime } from "@app/shared";
import { SortDirection } from "@app/shared/griddle/enumerators";
import { WafflesThemes } from "@app/shared/waffles/exports";

interface ClaimsInsightsSummaryTileProps {
  loading: boolean;
  getSummaries: (
    filtersPayload: PciDashboardFiltersPayload,
    summaryType: PciDashboardSummaryType,
    sortedColumn: string,
    sortDirection: SortDirection,
    isSorting?: boolean,
    isFiltering?: boolean,
  ) => void;
  onSummaryDashboardDetailsViewClick: (dashboardDetailsParam: string, summaryType: PciDashboardSummaryType) => void;
  summaryModel: ClaimsInsightsScoreSummaryModel;
  summaryType: PciDashboardSummaryType;
}

interface ClaimsInsightsSummaryTileToppings {
  effectivenessValue: string | JSX.Element;
  efficiencyValue: string | JSX.Element;
  key: string;
  summaryDescription: string;
  summaryName: JSX.Element;
}

const ClaimsInsightsSummaryTile = (props: ClaimsInsightsSummaryTileProps): JSX.Element => {
  const [buildingRecords, setBuildingRecords] = useState<boolean>(false);
  const [loadAllClicked, setLoadAllClicked] = useState<boolean>(false);
  const [summaryTileToppings, setSummaryTileToppings] = useState<ClaimsInsightsSummaryTileToppings[]>([]);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [sortedColumn, setSortedColumn] = useState<string>(null);

  const pciDashboardFiltersContext = useContext(PciDashboardFiltersContext);

  useEffect(() => {
    if (!!props.summaryModel?.summaries) {
      const waffleToppings: ClaimsInsightsSummaryTileToppings[] = props.summaryModel.summaries.map((s, index) => {
        /* eslint-disable sort-keys */
        return {
          key: `${s.summaryName}`,
          summaryName: (
            <EllipsisTooltip
              key={index}
              text={s.summaryName}
            />
          ),
          summaryDescription: s.summaryDescription,
          effectivenessValue:
            <QESButton
              onClick={() => props.onSummaryDashboardDetailsViewClick(
                props.summaryType === PciDashboardSummaryType.County ? s.summaryName : s.summaryDescription,
                props.summaryType
              )}
              qesTestId={`claims-insights-dashboard__summary--dashboard-details-effectiveness-${props.summaryType.toLowerCase()}-${index}`}
              qesType="secondary"
            >
              {getRoundingDisplay(s.effectivenessValue, false, 2)}
            </QESButton>,
          efficiencyValue:
            <QESButton
              onClick={() => props.onSummaryDashboardDetailsViewClick(
                props.summaryType === PciDashboardSummaryType.County ? s.summaryName : s.summaryDescription,
                props.summaryType
              )}
              qesTestId={`claims-insights-dashboard__summary--dashboard-details-efficiency-${props.summaryType.toLowerCase()}-${index}`}
              qesType="secondary"
            >
              {getRoundingDisplay(s.efficiencyValue, false, 2)}
            </QESButton>
        };
        /* eslint-enable sort-keys */
      });

      setSummaryTileToppings(waffleToppings);
    }
  }, [props.summaryModel, pciDashboardFiltersContext.displayedFilters]);

  useEffect(() => {
    // when sorting or loading in a large amount of records,
    // there could be some time between when the api call is complete
    // and when the records are visibly displayed
    const resizeObserver = new ResizeObserver((event) => {
      const resizeEntry = event[0]?.target?.children;
      const tbodyElement = [...resizeEntry]?.find(x => x.localName === "tbody");

      if (tbodyElement?.childElementCount === summaryTileToppings?.length && buildingRecords) {
        setLoadAllClicked(false);
        setBuildingRecords(false);
      }
    });

    const wafflesTable = document.querySelector(`[data-testid=claims-insights-dashboard__${props.summaryType}-summary--table]`);

    if (!!wafflesTable) {
      resizeObserver.observe(wafflesTable);
    }
  }, [summaryTileToppings]);

  useEffect(() => {
    if (props.loading && !!summaryTileToppings.length) {
      setSummaryTileToppings([]);
    }
  }, [props.loading]);

  const getClaimsInsightsProvidersToolTip = (columnName: string): string => {
    let claimsInsightsToolTip: string;
    switch (columnName) {
      case "effectiveness":
        claimsInsightsToolTip =
          "Effectiveness is a measure of quality where 5 is the best score (higher quality) and 1 is the worst score (lower quality). Scores are relative to other providers in the same specialty and state and are derived from claims insight into 1) Mortality 2) Complication 3) Days in acute care."
        break;
      case "efficiency":
        claimsInsightsToolTip =
          "Efficiency is a measure of physician cost vs. peers where 5 is the best score (lower cost) and 1 is the worst score (higher cost). Efficiency measures are based on 1) cost per visit 2) cost per patient. Standarized pricing methodology is used, meaning efficiency is a measure of comparitive utilization and not rate/price differences."
        break;
    }
    return claimsInsightsToolTip;
  }

  const columnConfig: ColumnConfig = {
    effectivenessValue: {
      alignment: TextAlignment.Center,
      displayText: "Effectiveness",
      testId: "effectiveness",
      tooltip: getClaimsInsightsProvidersToolTip("effectiveness")
    },
    efficiencyValue: {
      alignment: TextAlignment.Center,
      displayText: "Efficiency",
      testId: "efficiency",
      tooltip: getClaimsInsightsProvidersToolTip("efficiency")
    },
    summaryDescription: {
      alignment: TextAlignment.Left,
      displayText: props.summaryType === PciDashboardSummaryType.County ? "State" : "Code",
      testId: "summary-description",
    },
    summaryName: {
      alignment: TextAlignment.Left,
      displayText: props.summaryType,
      testId: "summary-name",
    },
  };

  const customSort = (
    records: ClaimsInsightsSummaryTileToppings[],
    column: keyof ClaimsInsightsSummaryTileToppings,
    sortedDirection: SortDirections
  ): ClaimsInsightsSummaryTileToppings[] => {
    let direction: SortDirection = null;

    switch (sortedDirection) {
      case SortDirections.Asc:
        direction = SortDirection.Ascending;
        break;
      case SortDirections.Desc:
        direction = SortDirection.Descending;
        break;
      case SortDirections.None:
        direction = SortDirection.None;
        break;
      default:
        direction = SortDirection.Unknown;
        break;
    }

    setSortDirection(direction);
    setSortedColumn(column);
    setBuildingRecords(true);

    props.getSummaries(null, props.summaryType, column, direction, true);

    return records;
  }

  const onLoadAllClick = () => {
    setLoadAllClicked(true);
    setBuildingRecords(true);
    props.getSummaries(null, props.summaryType, sortedColumn, sortDirection);
  }

  return (
    <div className="col-md-6 p0">
      <DashboardTile
        classNames="pci-dashboard__summary-tile"
        loading={props.loading}
        title={`${props.summaryType} Summary Averages - Network Providers`}
        subtitle={`As of ${formatDateTime(pciDashboardFiltersContext.projectRunConfiguration?.dateCreated)}`}
        widthClass=""
      >
        <Waffles<ClaimsInsightsSummaryTileToppings>
          columns={columnConfig}
          customSort={customSort}
          data={summaryTileToppings}
          fullWidth={true}
          passThroughSorting={true}
          testId={`claims-insights-dashboard__${props.summaryType}-summary--table`}
          theme={WafflesThemes.Scrollable}
        />

        {buildingRecords ? (
          <div className="d-flex justify-content-center mt-3">
            <LoadingSpinner />
          </div>
        ) : (
          (props.summaryModel?.totalCount > summaryTileToppings?.length && !loadAllClicked) && (
            <div className="d-flex justify-content-center mt-3">
              <QESButton
                onClick={() => onLoadAllClick()}
                qesTestId={`claims-insights-dashboard__${props.summaryType}-summary--load-all`}
                qesType="primary"
              >
                Load All
              </QESButton>
            </div>
          )
        )}
      </DashboardTile>
    </div>
  )
}

export default ClaimsInsightsSummaryTile;
